import uuidv4 from '../../../../util/uuid';
import {
  COMMAND_MODES,
  MAYBE_QUERY_COUNT,
  MAYBE_TIME_PER_VOTE_SEC,
  VOTE_UNDO_WINDOW_MS,
} from '../../constants/command';
import {
  canMatchFlushVote,
  deduplicateVotes,
  getMaybeVotingAppliedStatus,
} from '../../utilities/command';
import { trackCommandVoteEvent } from '../../utilities/analytics';
import { VOTE_DECISION } from '../../../../constants/vote-decisions';
import { LeasePossibleVotes } from '../../graphql/queries/votes';

export const MATCH_FETCH_VOTES_REQUEST = 'match/MATCH_FETCH_VOTES_REQUEST';
export const MATCH_FETCH_VOTES_SUCCESS = 'match/MATCH_FETCH_VOTES_SUCCESS';
export const MATCH_QUEUE_VOTES = 'match/MATCH_QUEUE_VOTES';
export const MATCH_COMMIT_VOTES = 'match/MATCH_COMMIT_VOTES';
export const MATCH_UNDO = 'match/MATCH_UNDO';
export const MATCH_CLEAR_REQUEST_TIMEOUTS =
  'match/MATCH_CLEAR_REQUEST_TIMEOUTS';
export const MATCH_QUEUE_RESET = 'match/MATCH_QUEUE_RESET';

const fetchVotesRequest = timeoutId => ({
  type: MATCH_FETCH_VOTES_REQUEST,
  timeoutId,
});

const fetchVotesSuccess = lease => ({
  type: MATCH_FETCH_VOTES_SUCCESS,
  lease,
});

/**
 * Clears all matching request timeouts.
 *
 * @returns {Function}
 */
export const clearMatchingRequestTimeouts = () => (dispatch, getState) => {
  const timeoutIds = getState().matchQueue.requestTimeoutIds;
  timeoutIds.forEach(clearTimeout);
  dispatch({ type: MATCH_CLEAR_REQUEST_TIMEOUTS });
};

/**
 * Resets the match queue (not the pending match queue though).
 *
 * @returns {function(*): {type: string}}
 */
export const matchQueueReset = () => dispatch => {
  dispatch(clearMatchingRequestTimeouts());
  dispatch({ type: MATCH_QUEUE_RESET });
};

/**
 * Action creator: Queues votes into pending status.
 * (Adds votes to the pending queue - the queue used by undo).
 * Votes here get commited (i.e. Actually mutated) after N seconds.
 *
 * @param {string} batchId The batch id to identify the current batch.
 * @param {object} selectedIdentity The selected identity.
 * @param {array<object>} votes The votes to be queued.
 * @param {number} timeoutId The setTimeout id.
 * @returns {{type: string, batchId: string, selectedIdentity: object, votes: array, timeoutId: number}}
 */
const queueVotes = (batchId, selectedIdentity, votes, timeoutId) => ({
  type: MATCH_QUEUE_VOTES,
  batchId,
  selectedIdentity,
  votes,
  timeoutId,
});

/**
 * Action creator: Commits votes, i.e. Removes them from pending queue
 * as they are considered commited.
 *
 * @param {string} batchId The vote batch id to remove.
 * @returns {{type: string, batchId: string}}
 */
const commitMatchingVotes = batchId => ({
  type: MATCH_COMMIT_VOTES,
  batchId,
});

/**
 * Action creator: Undos votes.
 *
 * @returns {{type: string}}
 */
const undoVotes = () => ({
  type: MATCH_UNDO,
});

/**
 * Fetches matching (maybe) votes.
 *
 * @param {Object} client - The Apollo client.
 * @param {number} delay - Optional delay.
 * @returns {function(*)}
 */
export const fetchMatchingVotes =
  (client, delay = 0) =>
  async dispatch => {
    const timeoutId = setTimeout(async () => {
      const {
        data: { leasePossibleVotes },
      } = await client.query({
        query: LeasePossibleVotes,
        variables: {
          count: MAYBE_QUERY_COUNT,
          time_per_vote: MAYBE_TIME_PER_VOTE_SEC,
        },
        fetchPolicy: 'network-only',
      });

      dispatch(
        fetchVotesSuccess({
          ...leasePossibleVotes,
          face_match_votes: deduplicateVotes(
            leasePossibleVotes.face_match_votes,
          ),
        }),
      );
    }, delay);

    dispatch(fetchVotesRequest(timeoutId));
  };

/**
 * Casts votes, adding them to the pending (undo) queue.
 *
 * @param {object} selectedIdentity The selected identity the votes belong to.
 * @param {array} votes The votes.
 * @param {function} bulkVetoVote The mutation.
 * @param {object} voteParams Vote parameters to pass into bulkVetoVote.
 * @returns {function(*)}
 */
export const castMatchingVotes =
  (selectedIdentity, votes, bulkVetoVote, voteParams) => dispatch => {
    const { user, decision, canMatchFlush } = voteParams;
    const batchId = uuidv4();

    // Matched POSSIBLE votes that are over the threshold
    // are immediately flushed. Applies to the whole cluster.
    // Only if the album allows for flushing (currently album.commander_enabled_flush).
    // and only if the votes are allowed to via canMatchFlushVote.
    const [vote] = votes;
    const matchFlush = !!canMatchFlush && canMatchFlushVote(vote);
    const applied =
      !!matchFlush && getMaybeVotingAppliedStatus(votes, decision);

    // Every batch of votes added to the pending queue
    // requires a timeoutId that will need to be cleared
    // when we `undoMatchingVotes`
    const timeoutId = setTimeout(async () => {
      await bulkVetoVote({ user, votes, decision, applied });

      trackCommandVoteEvent({
        mode: COMMAND_MODES.MATCH,
        user,
        decision,
        applied,
        votes,
        notSure: decision === VOTE_DECISION.WUT,
        custom: {
          dimension4: votes[0].cluster_id,
        },
      });

      dispatch(commitMatchingVotes(batchId));
    }, VOTE_UNDO_WINDOW_MS);

    dispatch(queueVotes(batchId, selectedIdentity, votes, timeoutId));
  };

/**
 * Undos the first batch of votes in the pending queue
 * by shifting them and clearing their timeout.
 *
 * @returns {function(*, *)}
 */
export const undoMatchingVotes = () => (dispatch, getState) => {
  const [head] = getState().matchQueue.pendingQueue;
  if (!head) {
    return;
  }

  clearTimeout(head.timeoutId);
  dispatch(undoVotes());
};
